import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { notifications } from '../data/mockData';

const NotificationsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  background-color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.cardBg};
  color: ${({ theme, active }) => active ? 'white' : theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.border};
  }
`;

const MarkAllReadButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}10`};
  }
`;

const NotificationsList = styled.div``;

const NotificationCard = styled.div`
  background-color: ${({ theme, unread }) => unread ? `${theme.colors.primary}05` : theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: flex-start;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme, type }) => {
    switch (type) {
      case 'like':
        return `${theme.colors.secondary}20`;
      case 'comment':
        return `${theme.colors.primary}20`;
      case 'friend':
        return `${theme.colors.success}20`;
      case 'group':
        return `${theme.colors.accent}20`;
      case 'mention':
        return `${theme.colors.error}20`;
      default:
        return theme.colors.cardBg;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-right: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const UserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

const GroupAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.cardBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

const UserName = styled(Link)`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-right: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const GroupName = styled(Link)`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-right: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NotificationAction = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NotificationText = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PostTitle = styled(Link)`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NotificationTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const NotificationActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ActionButton = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  
  &:hover {
    text-decoration: underline;
  }
`;

const UnreadIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  margin-left: auto;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const NotificationsPage = () => {
  const [filter, setFilter] = useState('all');
  const [notificationsState, setNotificationsState] = useState(
    notifications.map(notification => ({
      ...notification,
      read: notification.read
    }))
  );

  // Filter notifications based on selected filter
  const filteredNotifications = notificationsState.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });

  // Sort notifications by timestamp (newest first)
  const sortedNotifications = [...filteredNotifications].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  const handleMarkAsRead = (id) => {
    setNotificationsState(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotificationsState(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return '❤️';
      case 'comment':
        return '💬';
      case 'friend':
        return '👥';
      case 'group':
        return '👥';
      case 'mention':
        return '@️';
      default:
        return '🔔';
    }
  };

  // Format timestamp to relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    return notificationTime.toLocaleDateString();
  };

  return (
    <NotificationsContainer>
      <PageHeader>
        <PageTitle>Notifications</PageTitle>
        <MarkAllReadButton onClick={handleMarkAllAsRead}>
          Mark all as read
        </MarkAllReadButton>
      </PageHeader>

      <FilterContainer>
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All
        </FilterButton>
        <FilterButton
          active={filter === 'unread'}
          onClick={() => setFilter('unread')}
        >
          Unread
        </FilterButton>
        <FilterButton
          active={filter === 'like'}
          onClick={() => setFilter('like')}
        >
          Likes
        </FilterButton>
        <FilterButton
          active={filter === 'comment'}
          onClick={() => setFilter('comment')}
        >
          Comments
        </FilterButton>
        <FilterButton
          active={filter === 'friend'}
          onClick={() => setFilter('friend')}
        >
          Friend Requests
        </FilterButton>
        <FilterButton
          active={filter === 'group'}
          onClick={() => setFilter('group')}
        >
          Groups
        </FilterButton>
      </FilterContainer>

      <NotificationsList>
        {sortedNotifications.length > 0 ? (
          sortedNotifications.map(notification => (
            <NotificationCard
              key={notification.id}
              unread={!notification.read}
            >
              <NotificationIcon type={notification.type}>
                {getNotificationIcon(notification.type)}
              </NotificationIcon>

              <NotificationContent>
                <NotificationHeader>
                  {notification.type === 'group' ? (
                    <>
                      <GroupAvatar>{notification.group.icon}</GroupAvatar>
                      <GroupName to={`/group/${notification.group.id}`}>
                        {notification.group.name}
                      </GroupName>
                    </>
                  ) : (
                    <>
                      <UserAvatar src={notification.user.avatar} alt={notification.user.name} />
                      <UserName to={`/profile/${notification.user.username}`}>
                        {notification.user.name}
                      </UserName>
                    </>
                  )}
                  <NotificationAction>{notification.content}</NotificationAction>
                  {!notification.read && <UnreadIndicator />}
                </NotificationHeader>

                {notification.post && (
                  <NotificationText>
                    <PostTitle to={`/post/${notification.post.id}`}>
                      {notification.post.title}
                    </PostTitle>
                  </NotificationText>
                )}

                <NotificationTime>
                  {formatRelativeTime(notification.timestamp)}
                </NotificationTime>

                {!notification.read && (
                  <NotificationActions>
                    <ActionButton onClick={() => handleMarkAsRead(notification.id)}>
                      Mark as read
                    </ActionButton>
                  </NotificationActions>
                )}
              </NotificationContent>
            </NotificationCard>
          ))
        ) : (
          <EmptyState>
            <EmptyStateIcon>🔔</EmptyStateIcon>
            <EmptyStateTitle>No notifications</EmptyStateTitle>
            <EmptyStateText>
              {filter === 'all'
                ? "You don't have any notifications yet."
                : `You don't have any ${filter === 'unread' ? 'unread' : filter} notifications.`}
            </EmptyStateText>
          </EmptyState>
        )}
      </NotificationsList>
    </NotificationsContainer>
  );
};

export default NotificationsPage;
